import { type ClientId } from '../../client/bridge-api-client-types';

import { isTop } from './browser-apis';

/**
 * Resolves a target window by querying the DOM for an iframe with a specific data attribute.
 *
 * @param receiverId - The receiver ID to search for
 * @returns The iframe's contentWindow if found, otherwise undefined
 */
export const resolveChildIframeTarget = (receiverId: ClientId): Window | undefined => {
	if (typeof document === 'undefined') {
		return undefined;
	}
	const frame = document.querySelector<HTMLIFrameElement>(
		`[data-rovo-receiver-id="${receiverId}"]`,
	);

	if (!frame || !frame.contentWindow) {
		return undefined;
	}

	return frame.contentWindow;
};

/**
 * Resolves a target window by checking if communication should go to window.top.
 * This is used when the current window is inside an iframe and needs to communicate with the parent.
 *
 * @returns window.top if available and we're not at the top level, otherwise undefined
 */
export const resolveParentTarget = (): Window | undefined => {
	if (window.top && !isTop(window)) {
		return window.top;
	}

	return undefined;
};

/**
 * Finds a target window for cross-origin communication.
 * Tries multiple strategies in order:
 * 1. Look for a child iframe with the matching receiver ID
 * 2. Use window.top if we're inside an iframe
 *
 * @param receiverId - The receiver ID to find a target for
 * @returns The target window if found, otherwise undefined
 */
export const findCrossOriginTarget = (receiverId: ClientId): Window | undefined => {
	// Try to find a child iframe first (works at any level)
	const browserTarget = resolveChildIframeTarget(receiverId);
	if (browserTarget) {
		return browserTarget;
	}

	// If not a child, assume parent communication via window.top
	return resolveParentTarget();
};
